/**
 * The API client: transport, error mapping, and one function per backend route.
 * The typedefs mirror the backend's Pydantic schemas — keep them in step.
 */

import { config } from '@/config.js'

/* ------------------------------------------------------------------------- *
 * Types — mirrors of the backend's Pydantic schemas.
 * ------------------------------------------------------------------------- */

/**
 * @typedef {object} Identity
 * @property {string} ownerLabel sanitised upn, written on a preview's owner label
 * @property {string} name display name
 * @property {string} upn
 * @property {boolean} isAdmin resolved by the backend
 * @property {boolean} [authDisabled] the backend is running unauthenticated
 */

/**
 * @typedef {object} Component
 * @property {string} name e.g. `api`
 * @property {string} [displayName]
 * @property {string} [tagPath] the Helm path a tag override writes to
 * @property {string} [envPath] the Helm map variables write into, e.g. `api.env`.
 *   Null when the chart has no such map — which decides whether the component's
 *   variables can be edited, see lib/services.js
 */

/**
 * One host a preview answers on, from the Ingresses Argo CD reports.
 *
 * @typedef {object} ServiceLink
 * @property {string} label first DNS label, e.g. `rfi`
 * @property {string} url
 */

/**
 * One slot the form renders as a dropdown. Choosing nothing writes no values and leaves
 * the chart's own wiring in place.
 *
 * @typedef {object} DependencyInfo
 * @property {string} name e.g. `mezzo`
 * @property {string} displayName e.g. `Mezzo`
 * @property {string} application catalogue key whose previews may be chosen
 * @property {string} [defaultTarget] e.g. `mezzo.denci.internal`
 * @property {string} [description]
 */

/**
 * @typedef {object} Exposure
 * @property {string} name e.g. `public`
 * @property {string} displayName e.g. `Public`
 * @property {string} domainSuffix e.g. `denci.francemediasmonde.com`
 * @property {string} urlScheme `https`, or `http` where there are no certificates
 * @property {string} [description]
 * @property {boolean} [isDefault] preselected. Exactly one is true
 * @property {string[]} [deniedTagSuffixes] tags this exposure refuses, e.g. `-test`
 */

/**
 * @typedef {object} Application
 * @property {string} name e.g. `bach`
 * @property {string} displayName e.g. `Bach`
 * @property {Exposure[]} exposures where a preview can be published
 * @property {number} defaultTtlHours the lifetime the create form preselects
 * @property {number} [maxTtlHours] above which the API refuses a creation or a PATCH
 * @property {Component[]} components
 * @property {{repoURL: string, chart: string, valueFiles?: string[]}} [chart]
 * @property {DependencyInfo[]} [dependencies] one dropdown each
 */

/**
 * @typedef {object} Tag
 * @property {string} name
 * @property {string} [createdAt]
 */

/**
 * A published version of the umbrella chart: releases newest first, then pre-releases.
 *
 * @typedef {object} ChartVersion
 * @property {string} version e.g. `0.0.2`
 * @property {string} [createdAt]
 */

/**
 * @typedef {object} ResourceStatus
 * @property {string} kind `Deployment` | `Service` | `Ingress` | `StatefulSet`
 * @property {string} name
 * @property {string} health `Healthy` | `Progressing` | `Degraded` | `Missing` | `Suspended`
 * @property {string} [sync]
 * @property {string} [message]
 */

/**
 * @typedef {object} VersionSelection
 * @property {'simple'|'advanced'} mode
 * @property {string|null} tag set in simple mode; writes `global.image.tag`
 * @property {Record<string, string>} components set in advanced mode; writes `<svc>.image.tag`
 */

/**
 * @typedef {object} Preview
 * @property {string} name
 * @property {string} application
 * @property {string} [applicationDisplayName]
 * @property {string} owner
 * @property {string} [ownerUpn]
 * @property {string} [description]
 * @property {string} url
 * @property {ServiceLink[]} [links] the hosts it answers on when not on its own (FOV: one per
 *   brand); empty when `url` is the address. Read through `addressesOf()`
 * @property {string} namespace
 * @property {string} phase one of the six phases, see lib/status.js
 * @property {string} [health] raw Argo health
 * @property {string} [sync] raw Argo sync status
 * @property {string} createdAt
 * @property {string|null} expiresAt
 * @property {string} [chartVersion] the chart version this preview is pinned to
 * @property {string[]} [disabledComponents] components left out of this preview
 * @property {boolean} [followTags] Argo CD Image Updater redeploys on a re-pushed tag
 * @property {Record<string, string>} [followedDigests] the `sha256:` digest pinned per
 *   component. Detail only; empty until the updater's first cycle
 * @property {Record<string, Record<string, string>>} [env] per-service overrides, keyed
 *   by component name. Detail only: the list route does not carry it
 * @property {VersionSelection} version reconstructed by `versionFromWire()`; the only
 *   field here the API does not send under this name
 * @property {Record<string, string>} [dependencies] the preview each slot points at,
 *   keyed by slot name. On list rows too, so what points at a preview is read from them
 * @property {ResourceStatus[]} [resources]
 * @property {Array<{type: string, message?: string, lastTransitionTime?: string}>}
 *   [conditions] Argo's errors about the Application itself
 * @property {{phase?: string, message?: string, startedAt?: string, finishedAt?: string,
 *   revision?: string}} [operation]
 * @property {Record<string, any>} [values] the `valuesObject` PreviewApp generated
 * @property {{repoURL: string, chart: string, targetRevision: string}} [chart]
 * @property {boolean} [canModify] whether the caller may change it, computed by the backend
 */

/* ------------------------------------------------------------------------- *
 * Errors
 * ------------------------------------------------------------------------- */

/**
 * An API failure, carrying enough to be displayed as-is: users debug their own previews,
 * and that starts with showing them what the cluster said.
 */
export class ApiError extends Error {
  /**
   * @param {string} message user-facing
   * @param {object} [options]
   * @param {number} [options.status] HTTP status, 0 when the request never landed
   * @param {string} [options.code] backend error code
   * @param {string} [options.detail] raw backend/Kubernetes message
   */
  constructor(message, { status = 0, code = '', detail = '' } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }

  /** True when the backend could not be reached at all. */
  get isNetwork() {
    return this.status === 0
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  get isConflict() {
    return this.status === 409
  }
}

/* ------------------------------------------------------------------------- *
 * Transport
 * ------------------------------------------------------------------------- */

/** @type {() => Promise<string|null>} */
let tokenProvider = async () => null

/**
 * Register the function that returns a fresh access token. Set by the auth store, which
 * owns MSAL; injecting it keeps this module free of an auth dependency.
 *
 * @param {() => Promise<string|null>} provider
 */
export function setTokenProvider(provider) {
  tokenProvider = provider
}

/** @type {null | ((path: string, init: object) => Promise<any>)} */
let requestHandler = null

/**
 * Route every call to an in-memory handler instead of the network. For tests.
 *
 * @param {null | ((path: string, init: object) => Promise<any>)} handler
 */
export function setRequestHandler(handler) {
  requestHandler = handler
}

/**
 * @param {Record<string, any>} params
 * @returns {string} query string including `?`, or ''
 */
function queryString(params = {}) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

/**
 * The backend's `details` object as one line, for the technical footer. A validation
 * failure carries `{errors: [{field, message, type}]}`; everything else carries flat keys.
 */
function formatDetails(details) {
  if (!details || typeof details !== 'object') return ''
  const entries = Array.isArray(details.errors)
    ? details.errors.map((e) => [e.field, e.message])
    : Object.entries(details)
  return entries.map(([key, value]) => `${key}: ${value}`).join(', ')
}

/** Turn a non-2xx response into an ApiError with the most specific message available. */
async function toError(response) {
  let payload = null
  try {
    payload = await response.json()
  } catch {
    // Non-JSON error body (a gateway HTML page, most likely).
  }

  const fallback = {
    400: 'The request is invalid.',
    401: 'Your session has expired. Sign in again.',
    403: 'You are not allowed to perform this action.',
    404: 'This preview does not exist, or has already been deleted.',
    409: 'That name is already taken by an existing preview.',
    422: 'The request is invalid.',
    429: 'Too many requests. Try again in a moment.',
    500: 'The backend hit an internal error.',
    502: 'The backend is unreachable.',
    503: 'The backend is temporarily unavailable.',
    504: 'The backend did not answer in time.',
  }[response.status]

  return new ApiError(payload?.message || fallback || `Error ${response.status}.`, {
    status: response.status,
    code: payload?.code || '',
    detail: formatDetails(payload?.details),
  })
}

/**
 * Perform one API call.
 *
 * @param {string} path path under the API base, e.g. `/previews`
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {any} [options.body] serialised as JSON
 * @param {Record<string, any>} [options.params] query parameters
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>} parsed body, or null on 204
 */
export async function request(path, { method = 'GET', body, params, signal } = {}) {
  const url = `${path}${queryString(params)}`

  if (requestHandler) {
    return requestHandler(url, { method, body, signal })
  }

  const headers = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = await tokenProvider()
  if (token) headers.Authorization = `Bearer ${token}`

  let response
  try {
    response = await fetch(`${config.apiBaseUrl}${url}`, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause
    throw new ApiError('The backend is unreachable. Check your connection, then try again.', {
      status: 0,
      detail: String(cause?.message || cause),
    })
  }

  if (!response.ok) throw await toError(response)
  if (response.status === 204) return null

  const text = await response.text()
  return text ? JSON.parse(text) : null
}

/**
 * The wire's flat `globalTag` / `componentTags` back into the selection the form renders.
 * The mirror image of `versionToWire()` below.
 *
 * @param {{globalTag?: string, componentTags?: Record<string, string>}} raw
 * @returns {VersionSelection}
 */
function versionFromWire(raw) {
  const components = raw.componentTags || {}
  if (Object.keys(components).length) {
    return { mode: 'advanced', tag: null, components: { ...components } }
  }
  return { mode: 'simple', tag: raw.globalTag || null, components: {} }
}

/**
 * Add the form's view of the version to a preview. Everything else passes through under
 * the name the API gave it.
 *
 * @param {any} raw a `PreviewSummary` or a `PreviewDetail`
 * @returns {Preview}
 */
function withVersion(raw) {
  if (!raw || typeof raw !== 'object') return raw
  return { ...raw, version: versionFromWire(raw) }
}

/* ------------------------------------------------------------------------- *
 * Endpoints — one function per route in backend/README.md
 * ------------------------------------------------------------------------- */

/**
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<Identity>}
 */
export function getMe(options = {}) {
  return request('/me', options)
}

/**
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{items: Application[]}>}
 */
export function listApplications(options = {}) {
  return request('/applications', options)
}

/**
 * @param {string} application
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{items: ChartVersion[]}>}
 */
export function listChartVersions(application, options = {}) {
  return request(`/applications/${encodeURIComponent(application)}/chart-versions`, options)
}

/**
 * @param {string} application
 * @param {string} component
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{items: Tag[]}>}
 */
export function listComponentTags(application, component, options = {}) {
  return request(
    `/applications/${encodeURIComponent(application)}/components/${encodeURIComponent(component)}/tags`,
    options,
  )
}

/**
 * `application` and `owner` become label selectors server-side.
 *
 * @param {{application?: string, owner?: string, status?: string}} [filters]
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{items: Preview[]}>}
 */
export async function listPreviews(filters = {}, options = {}) {
  const payload = await request('/previews', { ...options, params: filters })
  return { ...payload, items: (payload?.items || []).map(withVersion) }
}

/**
 * @param {string} name
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<Preview>}
 */
export async function getPreview(name, options = {}) {
  return withVersion(await request(`/previews/${encodeURIComponent(name)}`, options))
}

/**
 * @typedef {object} CreatePreviewInput
 * @property {string} application
 * @property {string} name
 * @property {string} [description]
 * @property {string} [chartVersion] defaults to the newest published version
 * @property {string} [exposure] an exposure name, never a domain. Defaults to the
 *   catalogue's default exposure
 * @property {VersionSelection} version
 * @property {string[]} [disabledComponents] components to leave out of the preview
 * @property {boolean} [followTags]
 * @property {Record<string, Record<string, string>>} [env] per-component variables
 * @property {Record<string, string>} [dependencies] the preview each slot points at,
 *   keyed by slot name. A preview name, never a host
 * @property {number} ttlHours hours, or -1 for no expiry at all
 */

/**
 * The wire shape of a version selection: `globalTag` and `componentTags`, keyed by
 * component name so no request can decide which values path a tag lands on.
 *
 * @param {VersionSelection} [version]
 * @returns {{globalTag?: string, componentTags?: Record<string, string>}}
 */
function versionToWire(version) {
  if (!version) return {}
  if (version.mode === 'advanced') {
    const componentTags = Object.fromEntries(
      Object.entries(version.components || {}).filter(([, tag]) => tag),
    )
    return Object.keys(componentTags).length ? { componentTags } : {}
  }
  return version.tag ? { globalTag: version.tag } : {}
}

/**
 * Create a preview. The owner is taken from the validated token by the backend.
 * 409 when the name is already taken.
 *
 * @param {CreatePreviewInput} input
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<Preview>}
 */
export async function createPreview(input, options = {}) {
  const { version, description, chartVersion, ...rest } = input
  const body = {
    ...rest,
    ...versionToWire(version),
    // The API forbids unknown fields, and an empty description is not a description.
    ...(description ? { description } : {}),
    ...(chartVersion ? { chartVersion } : {}),
  }
  return withVersion(await request('/previews', { ...options, method: 'POST', body }))
}

/**
 * Change image tags, lifetime and/or description.
 *
 * `env` replaces one component's variables at a time — a component the body does not name
 * keeps what it had — which is deliberately not how `componentTags` behaves: the editor
 * saves one service at a time, and whole-set semantics would make every save carry data
 * up to 15 s stale from the detail poll.
 *
 * `dependencies` is the opposite: the whole set, so a slot left out of it goes back to
 * the chart's own wiring. There is one dropdown per slot on screen and they save
 * together, so there is no partial state to preserve.
 *
 * `valuesYaml` is the generated values, edited, as YAML. What PreviewApp writes must be left
 * as it is; anything else is an override, and one removed from the block is removed. The
 * API refuses lists, images and security settings.
 *
 * `exposure` moves a running preview: a new hostname, a new scheme and the chart's other
 * ingress value file. The URL changes with it, so links already shared stop working.
 *
 * `followTags: false` removes every digest pin; a tag or disabled-set change drops the pins
 * of the services it touches until the updater's next cycle.
 *
 * @param {string} name
 * @param {{version?: VersionSelection, env?: Record<string, Record<string, string>>,
 *   dependencies?: Record<string, string>, disabledComponents?: string[],
 *   exposure?: string, followTags?: boolean, ttlHours?: number,
 *   extendHours?: number, description?: string, valuesYaml?: string}} patch
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<Preview>}
 */
export async function updatePreview(name, patch, options = {}) {
  const { version, ...rest } = patch
  return withVersion(
    await request(`/previews/${encodeURIComponent(name)}`, {
      ...options,
      method: 'PATCH',
      body: { ...rest, ...versionToWire(version) },
    }),
  )
}

/**
 * Delete a preview. The Argo finalizer cascades to the namespace.
 *
 * @param {string} name
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<null>}
 */
export function deletePreview(name, options = {}) {
  return request(`/previews/${encodeURIComponent(name)}`, { ...options, method: 'DELETE' })
}

/**
 * Force a hard refresh of the Argo CD Application.
 *
 * @param {string} name
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<any>}
 */
export function syncPreview(name, options = {}) {
  return request(`/previews/${encodeURIComponent(name)}/sync`, { ...options, method: 'POST' })
}

/**
 * @typedef {object} ContainerProblem
 * @property {string} name
 * @property {string} reason e.g. `CrashLoopBackOff`, `ImagePullBackOff`, `OOMKilled`
 * @property {string|null} [message] the kubelet's message, or the termination message
 * @property {number|null} [exitCode]
 * @property {string|null} [lastReason] why it last stopped, when not `reason`
 * @property {number} restarts
 */

/**
 * @typedef {object} PodProblem
 * @property {string} name
 * @property {string|null} [workload] the Deployment, StatefulSet or Job it belongs to
 * @property {string|null} [phase]
 * @property {string|null} [reason] e.g. `Unschedulable`, `Evicted`
 * @property {string|null} [message]
 * @property {ContainerProblem[]} containers
 */

/**
 * @typedef {object} Warning
 * @property {string} [reason]
 * @property {string} [message]
 * @property {number} count
 * @property {string} [lastSeen]
 * @property {string} [objectKind]
 * @property {string} [objectName]
 */

/**
 * The pods that will not come up, and the namespace's recent warnings.
 *
 * @param {string} name
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{namespaceExists: boolean, pods: PodProblem[], warnings: Warning[]}>}
 */
export function getDiagnostics(name, options = {}) {
  return request(`/previews/${encodeURIComponent(name)}/diagnostics`, options)
}
